import { cn } from "@/lib/utils";

interface LogoProps {
  className?: string;
  showText?: boolean;
  showTagline?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "light" | "dark";
}

export const Logo = ({ 
  className, 
  showText = true,
  showTagline = false,
  size = "md",
  variant = "dark" 
}: LogoProps) => {
  const sizes = {
    sm: { icon: 36, text: "text-xl", tagline: "text-[8px]" },
    md: { icon: 44, text: "text-2xl lg:text-3xl", tagline: "text-[9px]" },
    lg: { icon: 60, text: "text-3xl lg:text-4xl", tagline: "text-[10px]" },
  };

  const colors = {
    light: {
      primary: "hsl(45, 30%, 96%)",
      accent: "hsl(40, 45%, 75%)",
      text: "text-cream",
      accentText: "text-gold-light",
    },
    dark: {
      primary: "hsl(var(--foreground))",
      accent: "hsl(var(--gold))",
      text: "text-foreground",
      accentText: "text-gradient-gold",
    },
  };

  const { icon, text, tagline } = sizes[size];
  const { primary, accent, text: textColor, accentText } = colors[variant];

  return (
    <a href="#" className={cn("group flex items-center gap-3", className)}>
      {/* Premium Monogram Icon */}
      <div className="relative">
        <svg
          width={icon}
          height={icon}
          viewBox="0 0 56 56"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="transition-transform duration-500 group-hover:scale-105"
        >
          {/* Outer diamond frame */}
          <path
            d="M28 2L54 28L28 54L2 28L28 2Z"
            stroke={accent}
            strokeWidth="1"
            fill="none"
            className="transition-all duration-500"
          />
          
          {/* Inner diamond */}
          <path
            d="M28 8L48 28L28 48L8 28L28 8Z"
            stroke={accent}
            strokeWidth="0.5"
            fill="none"
            opacity="0.4"
          />

          {/* Decorative top flourish */}
          <path
            d="M28 2L28 8M28 48L28 54"
            stroke={accent}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          
          {/* Decorative side flourishes */}
          <path
            d="M2 28L8 28M48 28L54 28"
            stroke={accent}
            strokeWidth="1.5"
            strokeLinecap="round"
          />

          {/* Letter A - refined serif */}
          <path
            d="M19 38L25 18H27L33 38H30.5L29 33.5H23L21.5 38H19ZM23.8 31H28.2L26 22L23.8 31Z"
            fill={primary}
          />
          
          {/* Letter E - elegant serif */}
          <path
            d="M35 18H42V20.5H37.5V26.5H41V29H37.5V35.5H42V38H35V18Z"
            fill={accent}
          />

          {/* Small decorative dots at corners */}
          <circle cx="28" cy="5" r="1" fill={accent} opacity="0.6" />
          <circle cx="51" cy="28" r="1" fill={accent} opacity="0.6" />
          <circle cx="28" cy="51" r="1" fill={accent} opacity="0.6" />
          <circle cx="5" cy="28" r="1" fill={accent} opacity="0.6" />
        </svg>
      </div>

      {/* Wordmark with optional tagline */}
      {showText && (
        <div className="flex flex-col">
          <span className={cn("font-display font-light tracking-wider leading-none", text, textColor)}>
            Agota
            <span className={cn("font-medium ml-1.5", accentText)}>Estate</span>
          </span>
          {showTagline && (
            <span className={cn(
              "font-body uppercase tracking-[0.25em] text-muted-foreground mt-1.5",
              tagline
            )}>
              Expertise · Integrity · Legacy
            </span>
          )}
        </div>
      )}
    </a>
  );
};

// Standalone monogram for favicon/brand mark usage
export const Monogram = ({ size = 56, className }: { size?: number; className?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    {/* Background */}
    <rect width="56" height="56" rx="4" fill="hsl(30, 15%, 10%)" />
    
    {/* Diamond frame */}
    <path
      d="M28 6L50 28L28 50L6 28L28 6Z"
      stroke="hsl(38, 55%, 55%)"
      strokeWidth="1"
      fill="none"
    />
    
    {/* Inner diamond */}
    <path
      d="M28 12L44 28L28 44L12 28L28 12Z"
      stroke="hsl(38, 55%, 55%)"
      strokeWidth="0.5"
      fill="none"
      opacity="0.4"
    />

    {/* Letter A */}
    <path
      d="M18 38L24 18H26L32 38H29.5L28 33.5H22L20.5 38H18ZM22.8 31H27.2L25 22L22.8 31Z"
      fill="hsl(40, 25%, 95%)"
    />
    
    {/* Letter E */}
    <path
      d="M34 18H41V20.5H36.5V26.5H40V29H36.5V35.5H41V38H34V18Z"
      fill="hsl(38, 55%, 55%)"
    />

    {/* Decorative dots */}
    <circle cx="28" cy="9" r="1" fill="hsl(38, 55%, 55%)" opacity="0.6" />
    <circle cx="47" cy="28" r="1" fill="hsl(38, 55%, 55%)" opacity="0.6" />
    <circle cx="28" cy="47" r="1" fill="hsl(38, 55%, 55%)" opacity="0.6" />
    <circle cx="9" cy="28" r="1" fill="hsl(38, 55%, 55%)" opacity="0.6" />
  </svg>
);
